import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useReaderStore } from '@/features/reader/stores/readerStore'
import { api, problemCode, problemDetail, problemStatus } from '@/services/api'

interface Usage {
  usage_remaining: number
  locked: boolean
  resets_at: string
}
interface Reply {
  answer: string
  found_material: boolean
  usage_remaining: number
  links: { label: string; book_id: string; page: number }[]
}

export function TutorPanel({ bookId }: { bookId: string }) {
  const [query, setQuery] = useState('')
  const [messages, setMessages] = useState<{ question: string; reply: Reply }[]>([])
  const client = useQueryClient()
  const go = useReaderStore((s) => s.irParaPagina)
  const close = useReaderStore((s) => s.abrirPainel)
  const usage = useQuery({
    queryKey: ['tutor-usage', bookId],
    queryFn: async () => (await api.get<Usage>('/tutor/usage')).data,
    retry: false,
    refetchInterval: 30000,
  })
  const send = useMutation({
    mutationFn: async (question: string) => (await api.post<Reply>('/tutor/chat', {
      query: question, book_id: bookId,
    })).data,
    onSuccess: (reply, question) => {
      setMessages((previous) => [...previous.slice(-9), { question, reply }])
      setQuery('')
    },
    onSettled: () => { void client.invalidateQueries({ queryKey: ['tutor-usage', bookId] }) },
  })
  const locked = usage.data?.locked || (usage.dataUpdatedAt < send.submittedAt && problemCode(send.error) === 'tutor_locked')
  const exhausted = usage.data?.usage_remaining === 0 || (usage.dataUpdatedAt < send.submittedAt && problemCode(send.error) === 'rate_limited')
  const disabled = !usage.data || usage.isError || locked || exhausted || send.isPending

  return <section className="tutor-panel" aria-label="Ajuda para estudar">
    <p>Vou ajudar você a encontrar pistas no livro e pensar no próximo passo. Não forneço respostas prontas.</p>
    {usage.isPending && <p role="status">Verificando disponibilidade…</p>}
    {usage.isError && <div role="alert"><p>{problemStatus(usage.error) === 404
      ? 'O Tutor ainda não está habilitado nesta plataforma.'
      : 'Não foi possível acessar o Tutor. Você pode continuar lendo e fazendo anotações.'}</p>
      <button type="button" onClick={() => { void usage.refetch() }}>Tentar novamente</button></div>}
    {locked && <p role="status">Desabilitado durante a avaliação. Volte após o prazo.</p>}
    {exhausted && <p role="status">Você atingiu o limite de hoje.</p>}
    {usage.data && <p>Restam {usage.data.usage_remaining} perguntas.
      {' '}O limite renova em {new Date(usage.data.resets_at).toLocaleString('pt-BR')}.</p>}
    <div className="tutor-messages" role="log" aria-label="Conversa de estudo" aria-live="polite">
      {messages.map(({ question, reply }, index) => <article key={index}>
        <p><strong>Sua dúvida:</strong> {question}</p>
        <p><strong>Tutor:</strong> {reply.answer}</p>
        {reply.links.map((link) => <button key={`${link.book_id}-${link.page}`} type="button"
          onClick={() => { go(link.page); close('nenhum') }}>
          Ir para {link.label}
        </button>)}
      </article>)}
    </div>
    {send.isError && <p role="alert">{problemDetail(send.error) ?? 'Não foi possível enviar. Tente novamente.'}</p>}
    <form onSubmit={(event) => { event.preventDefault(); if (!disabled && query.trim().length >= 3) send.mutate(query.trim()) }}>
      <label htmlFor="tutor-question">Em qual parte você precisa de ajuda?</label>
      <textarea id="tutor-question" value={query} onChange={(event) => setQuery(event.target.value)}
        maxLength={2000} minLength={3} required disabled={disabled} rows={4} />
      <button type="submit" disabled={disabled || query.trim().length < 3}>
        {send.isPending ? 'Buscando pistas…' : 'Pedir uma pista'}
      </button>
    </form>
    <p className="tutor-privacy">A conversa fica apenas neste painel e é apagada ao fechá-lo. Não inclua dados pessoais.</p>
  </section>
}
